import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_ENV, VITE_APP_RELEASE } from '../../lib/constants.ts';
import { useAppState } from '../../lib/hooks/use-app-state.ts';
import { api } from '../../utils/api.ts';
import { useAnalytics } from '../analytics/hooks/use-analytics.ts';

const POLL_INTERVAL_MS = 5_000 * 60;
const LINGER_DELAY_MS = 60_000;

const RELOADED_FOR_RELEASE_KEY = 'version-watch:reloaded-for-release';

const isMonitoredEnv = APP_ENV === 'production' || APP_ENV === 'staging';

function reloadedForRelease(): string | undefined {
    try {
        return sessionStorage.getItem(RELOADED_FOR_RELEASE_KEY) ?? undefined;
    } catch {
        return undefined;
    }
}

function markReloadedForRelease(release: string): void {
    try {
        sessionStorage.setItem(RELOADED_FOR_RELEASE_KEY, release);
    } catch {
        // sessionStorage blocked: the guard is skipped.
    }
}

export type UseVersionWatchHook = {
    showRefreshToast: boolean;
    dismiss: () => void;
};

/**
 * Detects a stale frontend (an open tab older than the deployed server) and nudges
 * onto the new build: reload on the next navigation, with a toast
 * backstop for users who linger
 */
export function useVersionWatch(): UseVersionWatchHook {
    const { platoUser } = useAppState();
    const sendEvent = useAnalytics();
    const { pathname } = useLocation();

    const shouldRun = isMonitoredEnv && !!VITE_APP_RELEASE && !!platoUser;

    const { data } = api.version.getCurrent.useQuery(undefined, {
        enabled: shouldRun,
        refetchOnMount: false,
        refetchOnWindowFocus: true,
        refetchInterval: POLL_INTERVAL_MS,
    });

    const stale =
        shouldRun &&
        !!data &&
        data.env === APP_ENV &&
        data.release !== 'unknown' &&
        data.release !== VITE_APP_RELEASE;

    const [lingered, setLingered] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const reportedReleaseRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!stale || reportedReleaseRef.current === data.release) {
            return;
        }
        reportedReleaseRef.current = data.release;
        sendEvent('stale_client_detected', {
            client_release: VITE_APP_RELEASE ?? 'unknown',
            server_release: data.release,
            app_env: APP_ENV,
        });
    }, [stale, data, sendEvent]);

    // Reload on the next navigation. The URL has already moved, so this loads the
    // destination on the fresh build.
    const prevPathnameRef = useRef(pathname);
    useEffect(() => {
        const navigated = prevPathnameRef.current !== pathname;
        prevPathnameRef.current = pathname;
        if (stale && navigated && reloadedForRelease() !== data.release) {
            markReloadedForRelease(data.release);
            window.location.reload();
        }
    }, [stale, pathname, data]);

    // Backstop for users who linger without navigating
    useEffect(() => {
        if (!stale) {
            return;
        }
        const timeout = setTimeout(() => {
            setLingered(true);
        }, LINGER_DELAY_MS);
        return () => {
            clearTimeout(timeout);
        };
    }, [stale]);

    return {
        showRefreshToast: stale && lingered && !dismissed,
        dismiss: () => {
            setDismissed(true);
        },
    };
}
